import { AnalyticsUserEvent } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { router } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    type GestureResponderEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Pressable,
    ScrollView,
} from 'react-native';
import Carousel, {
    type ICarouselInstance,
} from 'react-native-reanimated-carousel';
import {
    BodyMediumRegular,
    FrostedSurface,
    FruitfulCustomIcon,
    H1Emphasized,
    Icon,
    isNonMobileWeb,
    LoadingSkeleton,
    SettingsIcon,
    Shadows,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'smoothie';

import useGetAllBankAccountDetails from '../../../banking/hooks/useGetAllBankAccountDetails';
import {
    AccountType,
    type BankAccountDisplayDetail,
} from '../../../banking/types/BankAccount';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { getDefaultedAccountNickname } from '../../../common/utils/bankingUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { track } from '../../../logger/Logger';
import { useGetFreeToSpendData } from '../../hooks/useGetFreeToSpendData';
import {
    FreeToSpendPeriodAtom,
    SelectedFreeToSpendAccountIndex,
} from '../../state/FreeToSpendAtoms';
import type { FreeToSpendAccountBalanceItem } from '../../types/FreeToSpendTypes';
import { SpendAccountSettingsSheet } from './SpendAccountSettingsSheet';

export interface AccountBalancesCarouselProps {
    containerProps?: StackProps;
}

/** Horizontal space between carousel balance cards ($3 spacing), mobile two-account peek. */
const CAROUSEL_ITEM_GAP = 12;

/** Space between adjacent account cards on desktop web ($6); applied as gutter after each full-width slide. */
const CAROUSEL_DESKTOP_INTER_ITEM_GAP = 24;

/** Inset inside each carousel slide on mobile (native & mobile web); balances width vs gutters. */
const CAROUSEL_MOBILE_HORIZONTAL_INSET = 22;

/** Card content height inside the carousel row. */
const ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT = 135;

/** Room below cards so Shadows.SMALL is not clipped (offset + blur / elevation bleed). */
const ACCOUNT_BALANCE_CAROUSEL_SHADOW_OVERFLOW = 16;

const ACCOUNT_BALANCE_CAROUSEL_ROW_HEIGHT =
    ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT +
    ACCOUNT_BALANCE_CAROUSEL_SHADOW_OVERFLOW;

const DRAG_TAP_THRESHOLD = 10;

// this ensures that the tap is not triggered if the user is scrolling
const useTapToClick = (onTap?: () => void) => {
    const startRef = useRef<{ x: number; y: number } | null>(null);

    const handlePressIn = useCallback(
        (event: GestureResponderEvent) => {
            if (onTap == null) return;
            const { pageX, pageY } = event.nativeEvent;
            startRef.current = { x: pageX, y: pageY };
        },
        [onTap],
    );

    const handlePressOut = useCallback(
        (event: GestureResponderEvent) => {
            if (onTap == null) return;
            const start = startRef.current;
            startRef.current = null;
            if (start == null) return;

            const { pageX, pageY } = event.nativeEvent;
            const dx = Math.abs(pageX - start.x);
            const dy = Math.abs(pageY - start.y);

            if (dx < DRAG_TAP_THRESHOLD && dy < DRAG_TAP_THRESHOLD) {
                onTap();
            }
        },
        [onTap],
    );

    return {
        onPressIn: handlePressIn,
        onPressOut: handlePressOut,
    };
};

const AccountBalanceCard = ({
    account,
    onSettingsPress,
    frosted = false,
}: {
    account: FreeToSpendAccountBalanceItem;
    onSettingsPress?: () => void;
    frosted?: boolean;
}) => {
    const accountTapHandlers = useTapToClick(() => {
        track(AnalyticsUserEvent.FTS_TAP_ACCOUNT);
        router.push(
            `/banking/space-details?syncteraAccountId=${account.syncteraAccountId}`,
        );
    });

    const settingsTapHandlers = useTapToClick(onSettingsPress);

    const content = (
        <>
            <XStack justifyContent="space-between">
                <XStack
                    gap={8}
                    alignItems="center"
                    cursor="pointer"
                    {...accountTapHandlers}
                >
                    <FruitfulCustomIcon
                        icon={account.icon}
                        color={account.color}
                        size="xs"
                    />
                    <BodyMediumRegular color="$gray1200">
                        {account.name}
                    </BodyMediumRegular>
                </XStack>
                {onSettingsPress != null && (
                    <Pressable
                        {...settingsTapHandlers}
                        style={{ padding: 4 }}
                        hitSlop={8}
                    >
                        <Icon icon={SettingsIcon} color="$gray900" size={24} />
                    </Pressable>
                )}
            </XStack>
            <Pressable
                {...accountTapHandlers}
                style={{
                    minHeight: 48,
                    justifyContent: 'center',
                    alignSelf: 'flex-start',
                }}
            >
                <H1Emphasized color="$gray1200">
                    {account.availableBalance}
                </H1Emphasized>
            </Pressable>
        </>
    );

    // GOYO: match the frosted background styling of the home calendar widget.
    if (frosted) {
        return (
            <FrostedSurface flex={1} borderRadius={24}>
                <YStack flex={1} padding={24} gap={8}>
                    {content}
                </YStack>
            </FrostedSurface>
        );
    }

    return (
        <View
            flex={1}
            backgroundColor={isNonMobileWeb ? '$gray200' : '$white'}
            borderRadius={24}
            padding={24}
            gap={8}
            {...(isNonMobileWeb ? {} : Shadows.MEDIUM)}
        >
            {content}
        </View>
    );
};

const LoadingAccountBalanceCard = ({
    frosted = false,
}: {
    frosted?: boolean;
}) => {
    const content = (
        <>
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={8} alignItems="center">
                    <LoadingSkeleton width={32} height={32} borderRadius={8} />
                    <LoadingSkeleton width={110} height={24} borderRadius={8} />
                </XStack>
                <LoadingSkeleton width={32} height={32} borderRadius={12} />
            </XStack>
            <LoadingSkeleton width={140} height={48} borderRadius={8} />
        </>
    );

    if (frosted) {
        return (
            <FrostedSurface flex={1} borderRadius={24}>
                <YStack flex={1} padding={24} gap={8}>
                    {content}
                </YStack>
            </FrostedSurface>
        );
    }

    return (
        <View
            flex={1}
            backgroundColor={isNonMobileWeb ? '$gray200' : '$white'}
            borderRadius={24}
            padding={24}
            gap={8}
            {...(isNonMobileWeb ? {} : Shadows.MEDIUM)}
        >
            {content}
        </View>
    );
};

export const AccountBalancesCarousel = ({
    containerProps,
}: AccountBalancesCarouselProps) => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetails(true);
    // Non-GOYO still relies on SpendingSummary to populate the atom (enabled
    // defaults to false). Under GOYO, SpendingSummary isn't rendered, so the
    // carousel fetches its own free-to-spend data.
    const { data, isLoading } = useGetFreeToSpendData(goyoEnabled);
    const carouselRef = useRef<ICarouselInstance>(null);
    const scrollRef = useRef<ScrollView>(null);
    const isProgrammaticScrollRef = useRef(false);
    const lastScrollIndexRef = useRef(0);
    const dragStartIndexRef = useRef(0);
    const lastSettledIndexRef = useRef(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSettingsSheetOpen, setIsSettingsSheetOpen] = useState(false);
    const [
        selectedFreeToSpendAccountIndex,
        setSelectedFreeToSpendAccountIndex,
    ] = useAtom(SelectedFreeToSpendAccountIndex);
    const setFreeToSpendPeriod = useSetAtom(FreeToSpendPeriodAtom);

    const accountBalances = useMemo(() => {
        if (bankAccountDetails == null || isLoadingBankAccountDetails) {
            return [];
        }

        const fromFreeToSpend =
            data != null
                ? Object.values(data).map((acct) => ({
                      syncteraAccountId: acct.syncteraAccountId,
                      name: acct.name,
                      icon: acct.icon,
                      color: acct.color,
                      availableBalance:
                          bankAccountDetails.find(
                              (b) =>
                                  b.syncteraAccountId ===
                                  acct.syncteraAccountId,
                          )?.availableToWithdraw ?? '',
                  }))
                : [];

        // GOYO: if free-to-spend has no configured spend account but the member
        // has a Fruitful Spend (checking) account, still surface it as a single
        // widget (not a carousel). Prefer a checking account whose title
        // mentions "spend" (the member's actual spend account) over any other
        // checking account, so we don't surface an unrelated account (e.g.
        // "Core" at $0.00) when free-to-spend has no configured account yet.
        // Within that, prefer the member's own individual account, then fall
        // back to the first checking account. The balance falls back to the
        // account's real available balance (e.g. $0.00).
        if (fromFreeToSpend.length === 0 && goyoEnabled) {
            const checkingAccounts = bankAccountDetails.filter(
                (b) => b.accountType === AccountType.CHECKING,
            );
            const memberOwnedChecking = checkingAccounts.filter(
                (b) => b.jointUser == null && b.primaryUser?.id === user.id,
            );
            const titleHasSpend = (b: BankAccountDisplayDetail) =>
                getDefaultedAccountNickname(b).toLowerCase().includes('spend');

            const spendAccount =
                memberOwnedChecking.find(titleHasSpend) ??
                checkingAccounts.find(titleHasSpend) ??
                memberOwnedChecking[0] ??
                checkingAccounts[0];

            return spendAccount != null
                ? [
                      {
                          syncteraAccountId: spendAccount.syncteraAccountId,
                          name: getDefaultedAccountNickname(spendAccount),
                          icon: spendAccount.icon ?? '',
                          color: spendAccount.color ?? '',
                          availableBalance: spendAccount.availableToWithdraw,
                      },
                  ]
                : [];
        }

        return fromFreeToSpend;
    }, [
        data,
        JSON.stringify(bankAccountDetails),
        isLoadingBankAccountDetails,
        goyoEnabled,
        user.id,
    ]);

    const handleOpenSettings = useCallback(() => {
        track(AnalyticsUserEvent.FTS_OPEN_SETTINGS);
        setIsSettingsSheetOpen(true);
    }, []);

    const secondSnapOffset =
        containerWidth > 0
            ? containerWidth -
              CAROUSEL_MOBILE_HORIZONTAL_INSET -
              CAROUSEL_ITEM_GAP
            : 0;

    const scrollToIndex = useCallback(
        (index: number) => {
            if (
                !isNonMobileWeb &&
                accountBalances.length === 2 &&
                containerWidth > 0
            ) {
                isProgrammaticScrollRef.current = true;
                lastScrollIndexRef.current = index;
                scrollRef.current?.scrollTo({
                    x: index === 0 ? 0 : secondSnapOffset,
                    animated: true,
                });
            } else {
                isProgrammaticScrollRef.current = true;
                carouselRef.current?.scrollTo({ index, animated: true });
            }
            setCurrentIndex(index);
            setSelectedFreeToSpendAccountIndex(index);
        },
        [
            setSelectedFreeToSpendAccountIndex,
            accountBalances.length,
            containerWidth,
            secondSnapOffset,
        ],
    );

    const onSwipeSnapToItem = useCallback(() => {
        track(AnalyticsUserEvent.FTS_SWIPE_CARD);
    }, []);

    const handleTwoAccountScroll = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (isProgrammaticScrollRef.current) return;
            const x = e.nativeEvent.contentOffset.x;
            const index = x < secondSnapOffset / 2 ? 0 : 1;
            lastScrollIndexRef.current = index;
            lastSettledIndexRef.current = index;
            setCurrentIndex(index);
            setSelectedFreeToSpendAccountIndex(index);
        },
        [setSelectedFreeToSpendAccountIndex, secondSnapOffset],
    );

    const handleTwoAccountMomentumScrollEnd = useCallback(
        (e: NativeSyntheticEvent<NativeScrollEvent>) => {
            const wasProgrammatic = isProgrammaticScrollRef.current;
            if (!wasProgrammatic) {
                const x = e.nativeEvent.contentOffset.x;
                const index = x < secondSnapOffset / 2 ? 0 : 1;
                if (index !== dragStartIndexRef.current) {
                    onSwipeSnapToItem();
                }
            }
            isProgrammaticScrollRef.current = false;
            handleTwoAccountScroll(e);
        },
        [handleTwoAccountScroll, onSwipeSnapToItem, secondSnapOffset],
    );

    const handleSnapToItem = useCallback(
        (index: number) => {
            const wasProgrammatic = isProgrammaticScrollRef.current;
            isProgrammaticScrollRef.current = false;
            if (!wasProgrammatic && index !== lastSettledIndexRef.current) {
                onSwipeSnapToItem();
            }
            lastSettledIndexRef.current = index;
            setCurrentIndex(index);
            setSelectedFreeToSpendAccountIndex(index);
        },
        [
            onSwipeSnapToItem,
            setSelectedFreeToSpendAccountIndex,
            setCurrentIndex,
        ],
    );

    useEffect(() => {
        if (
            selectedFreeToSpendAccountIndex === undefined ||
            selectedFreeToSpendAccountIndex < 0 ||
            selectedFreeToSpendAccountIndex >= accountBalances.length
        ) {
            return;
        }
        if (
            !isNonMobileWeb &&
            accountBalances.length === 2 &&
            containerWidth > 0
        ) {
            if (lastScrollIndexRef.current === selectedFreeToSpendAccountIndex)
                return;
            if (containerWidth > 0 && secondSnapOffset > 0) {
                isProgrammaticScrollRef.current = true;
                lastScrollIndexRef.current = selectedFreeToSpendAccountIndex;
                scrollRef.current?.scrollTo({
                    x:
                        selectedFreeToSpendAccountIndex === 0
                            ? 0
                            : secondSnapOffset,
                    animated: true,
                });
                setCurrentIndex(selectedFreeToSpendAccountIndex);
            }
        } else if (
            containerWidth > 0 &&
            (carouselRef.current?.getCurrentIndex() ?? -1) !==
                selectedFreeToSpendAccountIndex
        ) {
            isProgrammaticScrollRef.current = true;
            carouselRef.current?.scrollTo({
                index: selectedFreeToSpendAccountIndex,
                animated: true,
            });
            setCurrentIndex(selectedFreeToSpendAccountIndex);
        }
    }, [
        selectedFreeToSpendAccountIndex,
        accountBalances.length,
        containerWidth,
        secondSnapOffset,
    ]);

    // Under GOYO the card shows purely based on having a spend account (the
    // empty-balances guard below), not on completing the Money Map.
    if (!goyoEnabled && !user.memberJourneyLog?.completedMoneyMap) {
        return null;
    }

    // Wait for the free-to-spend fetch to settle (same as non-GOYO) so we show
    // the member's *configured* spend account rather than flashing the GOYO
    // first-checking fallback. The fallback only applies once free-to-spend has
    // settled empty.
    const isLoadingAccountBalances = isLoading || isLoadingBankAccountDetails;

    if (!isLoadingAccountBalances && accountBalances.length === 0) return null;

    const mobileCardInnerWidth = Math.max(
        0,
        containerWidth - 2 * CAROUSEL_MOBILE_HORIZONTAL_INSET,
    );

    const hasSize =
        containerWidth > 0 &&
        (!isNonMobileWeb && accountBalances.length === 2 && containerWidth > 0
            ? containerWidth - CAROUSEL_MOBILE_HORIZONTAL_INSET > 0
            : (containerWidth > 0
                  ? isNonMobileWeb
                      ? containerWidth
                      : !isNonMobileWeb &&
                          accountBalances.length === 2 &&
                          containerWidth > 0
                        ? containerWidth - CAROUSEL_MOBILE_HORIZONTAL_INSET
                        : containerWidth - 2 * CAROUSEL_MOBILE_HORIZONTAL_INSET
                  : 0) > 0);

    const isDesktopMultiAccountCarousel =
        isNonMobileWeb && accountBalances.length > 1;

    const carouselSlideWidth =
        isDesktopMultiAccountCarousel && containerWidth > 0
            ? containerWidth + CAROUSEL_DESKTOP_INTER_ITEM_GAP
            : !isNonMobileWeb &&
                accountBalances.length === 2 &&
                containerWidth > 0
              ? containerWidth - CAROUSEL_MOBILE_HORIZONTAL_INSET
              : containerWidth;

    return (
        <YStack
            width="100%"
            paddingBottom={accountBalances.length > 1 ? 28 : 0}
            alignSelf="center"
            onLayout={(e) => {
                setContainerWidth(e.nativeEvent.layout.width);
            }}
            {...containerProps}
        >
            <View width="100%">
                {isLoadingAccountBalances && (
                    <View
                        width="100%"
                        height={ACCOUNT_BALANCE_CAROUSEL_ROW_HEIGHT}
                        paddingHorizontal={
                            isNonMobileWeb
                                ? 0
                                : CAROUSEL_MOBILE_HORIZONTAL_INSET
                        }
                        justifyContent="flex-start"
                        alignItems={isNonMobileWeb ? 'stretch' : 'center'}
                    >
                        <View
                            height={ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT}
                            width={
                                isNonMobileWeb
                                    ? undefined
                                    : mobileCardInnerWidth || undefined
                            }
                            flex={isNonMobileWeb ? 1 : undefined}
                            alignSelf={isNonMobileWeb ? 'stretch' : undefined}
                        >
                            <LoadingAccountBalanceCard
                                frosted={goyoEnabled && !isNonMobileWeb}
                            />
                        </View>
                    </View>
                )}
                {/* Mobile carousel is custom to show preview of other account */}
                {!isLoadingAccountBalances &&
                    hasSize &&
                    !isNonMobileWeb &&
                    accountBalances.length === 2 &&
                    containerWidth > 0 && (
                        <ScrollView
                            ref={scrollRef}
                            horizontal
                            pagingEnabled={false}
                            showsHorizontalScrollIndicator={false}
                            snapToOffsets={[0, secondSnapOffset]}
                            snapToAlignment="start"
                            decelerationRate="fast"
                            onScrollBeginDrag={() => {
                                dragStartIndexRef.current =
                                    lastScrollIndexRef.current;
                            }}
                            onScroll={handleTwoAccountScroll}
                            onMomentumScrollEnd={
                                handleTwoAccountMomentumScrollEnd
                            }
                            scrollEventThrottle={16}
                            style={{ width: containerWidth }}
                        >
                            {accountBalances.map((item, index) => {
                                const isFirst = index === 0;
                                return (
                                    <View
                                        key={item.syncteraAccountId}
                                        style={{
                                            width:
                                                containerWidth -
                                                CAROUSEL_MOBILE_HORIZONTAL_INSET,
                                            ...(isFirst
                                                ? {
                                                      paddingLeft:
                                                          CAROUSEL_MOBILE_HORIZONTAL_INSET,
                                                      marginRight:
                                                          CAROUSEL_ITEM_GAP,
                                                  }
                                                : {
                                                      paddingRight:
                                                          CAROUSEL_MOBILE_HORIZONTAL_INSET,
                                                  }),
                                            height: ACCOUNT_BALANCE_CAROUSEL_ROW_HEIGHT,
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <View
                                            style={{
                                                width:
                                                    containerWidth > 0
                                                        ? mobileCardInnerWidth
                                                        : 0,
                                                height: ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT,
                                            }}
                                        >
                                            <AccountBalanceCard
                                                account={item}
                                                onSettingsPress={
                                                    handleOpenSettings
                                                }
                                                frosted={
                                                    goyoEnabled &&
                                                    !isNonMobileWeb
                                                }
                                            />
                                        </View>
                                    </View>
                                );
                            })}
                        </ScrollView>
                    )}
                {/* Web carousel uses package to ensure correct web scrolling behavior */}
                {!isLoadingAccountBalances &&
                    hasSize &&
                    !(
                        !isNonMobileWeb &&
                        accountBalances.length === 2 &&
                        containerWidth > 0
                    ) && (
                        <View
                            width={
                                isDesktopMultiAccountCarousel
                                    ? containerWidth
                                    : '100%'
                            }
                            overflow={
                                isDesktopMultiAccountCarousel
                                    ? 'hidden'
                                    : undefined
                            }
                        >
                            <Carousel
                                ref={carouselRef}
                                width={carouselSlideWidth}
                                height={ACCOUNT_BALANCE_CAROUSEL_ROW_HEIGHT}
                                data={accountBalances}
                                loop={false}
                                onSnapToItem={handleSnapToItem}
                                renderItem={({ item }) =>
                                    isDesktopMultiAccountCarousel ? (
                                        <XStack
                                            width={carouselSlideWidth}
                                            height={
                                                ACCOUNT_BALANCE_CAROUSEL_ROW_HEIGHT
                                            }
                                            alignItems="stretch"
                                        >
                                            <View
                                                width={containerWidth}
                                                justifyContent="flex-start"
                                            >
                                                <View
                                                    height={
                                                        ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT
                                                    }
                                                    flex={1}
                                                >
                                                    <AccountBalanceCard
                                                        account={item}
                                                        onSettingsPress={
                                                            handleOpenSettings
                                                        }
                                                        frosted={
                                                            goyoEnabled &&
                                                            !isNonMobileWeb
                                                        }
                                                    />
                                                </View>
                                            </View>
                                            <View
                                                width={
                                                    CAROUSEL_DESKTOP_INTER_ITEM_GAP
                                                }
                                            />
                                        </XStack>
                                    ) : (
                                        <View
                                            width={carouselSlideWidth}
                                            justifyContent="flex-start"
                                            alignItems={
                                                isNonMobileWeb
                                                    ? 'stretch'
                                                    : 'center'
                                            }
                                            paddingHorizontal={
                                                isNonMobileWeb
                                                    ? 0
                                                    : CAROUSEL_MOBILE_HORIZONTAL_INSET
                                            }
                                        >
                                            <View
                                                height={
                                                    ACCOUNT_BALANCE_CAROUSEL_CONTENT_HEIGHT
                                                }
                                                width={
                                                    isNonMobileWeb
                                                        ? undefined
                                                        : mobileCardInnerWidth
                                                }
                                                flex={
                                                    isNonMobileWeb
                                                        ? 1
                                                        : undefined
                                                }
                                                alignSelf={
                                                    isNonMobileWeb
                                                        ? 'stretch'
                                                        : undefined
                                                }
                                            >
                                                <AccountBalanceCard
                                                    account={item}
                                                    onSettingsPress={
                                                        handleOpenSettings
                                                    }
                                                    frosted={
                                                        goyoEnabled &&
                                                        !isNonMobileWeb
                                                    }
                                                />
                                            </View>
                                        </View>
                                    )
                                }
                            />
                        </View>
                    )}
                {/* Pagination buttons */}
                {!isLoadingAccountBalances &&
                    hasSize &&
                    accountBalances.length > 1 && (
                        <View
                            position="absolute"
                            bottom={-20}
                            alignSelf="center"
                            flexDirection="row"
                            gap={8}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {accountBalances.map((_, index) => (
                                <Pressable
                                    key={index}
                                    onPress={() => {
                                        scrollToIndex(index);
                                    }}
                                    style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor:
                                            index ===
                                            (selectedFreeToSpendAccountIndex ??
                                                currentIndex)
                                                ? '#111'
                                                : isNonMobileWeb
                                                  ? '#e4e4e4'
                                                  : '#fff',
                                    }}
                                />
                            ))}
                        </View>
                    )}
            </View>
            <SpendAccountSettingsSheet
                isOpen={isSettingsSheetOpen}
                onClose={() => {
                    setIsSettingsSheetOpen(false);
                }}
                onPeriodChange={setFreeToSpendPeriod}
                hasMultipleAccounts={accountBalances.length > 1}
            />
        </YStack>
    );
};
